/**
 * GameModule das den SunTimerBillboard-Renderer mit Daten füttert.
 * Wandelt Stern-Events und Lifecycle-Daten in Instanzen und Atlas-Text um.
 * State-abhängige Farbkodierung: Grün (Alive), Orange (Imploding),
 * Rot (Exploding), Lila (BlackHole). Toggle mit Taste 'T'.
 */

import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { SunLifecycleEntry } from '../systems/SunLifecycleTypes';
import type { ClusterContent } from '../network/ContentLoader';
import type { SunTimerBillboard } from '../objects/SunTimerBillboard';
import { SunState } from '../systems/SunLifecycleTypes';
import { axialToWorld } from '../math/HexMath';

interface SunPos { entityId: string; wx: number; wz: number; baseSize: number }

/** RGB-Farben je Lifecycle-State (0-1). */
const STATE_COLORS: Record<number, readonly [number, number, number]> = {
  [SunState.Alive]: [0.3, 0.9, 0.4],
  [SunState.Imploding]: [1.0, 0.65, 0.1],
  [SunState.Exploding]: [1.0, 0.2, 0.15],
  [SunState.CollapsingToBlackHole]: [0.7, 0.2, 0.9],
  [SunState.BlackHole]: [0.5, 0.1, 0.8],
  [SunState.Dead]: [0.4, 0.4, 0.4],
};
const STATE_NAMES: Record<number, string> = {
  [SunState.Alive]: 'ALIVE',
  [SunState.Imploding]: 'IMPLODING',
  [SunState.Exploding]: 'EXPLODING',
  [SunState.CollapsingToBlackHole]: 'COLLAPSING',
  [SunState.BlackHole]: 'BLACKHOLE',
  [SunState.Dead]: 'DEAD',
};

const DEFAULT_COLOR: readonly [number, number, number] = [0.3, 0.9, 0.4];

/** States bei denen der Timer nicht mehr angezeigt wird. */
const HIDDEN_STATES = new Set<number>([SunState.Dead, SunState.BlackHole]);

export class SunTimerOverlay implements GameModule {
  readonly name = 'SunTimerOverlay';

  private _billboard: SunTimerBillboard;
  private _hexRadius: number;
  private _sunBaseSize: number;
  private _sunPositions: SunPos[] = [];
  private _lifecycle: ReadonlyMap<string, SunLifecycleEntry> | null = null;
  private _visible = false;
  private _textTimer = 1;

  constructor(billboard: SunTimerBillboard, hexRadius: number, sunBaseSize: number) {
    this._billboard = billboard;
    this._hexRadius = hexRadius;
    this._sunBaseSize = sunBaseSize;
  }

  init(engine: Engine): void {
    engine.events.on<ClusterContent[]>('sun:stars-available', (c) => this._updatePositions(c));
    engine.events.on<ReadonlyMap<string, SunLifecycleEntry>>('sun:lifecycle-update', (d) => {
      this._lifecycle = d;
    });
    document.addEventListener('keydown', this._onKey);
  }

  private _onKey = (e: KeyboardEvent): void => {
    if (e.key === 't' || e.key === 'T') {
      this._visible = !this._visible;
      this._billboard.visible = this._visible;
      if (this._visible) this._textTimer = 1;
    }
  };

  private _updatePositions(contents: ClusterContent[]): void {
    this._sunPositions.length = 0;
    const hr = this._hexRadius;
    const base = this._sunBaseSize;
    for (let c = 0; c < contents.length; c++) {
      const stars = contents[c].stars;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        if (s.axial_q == null || s.axial_r == null || !s.entity_id) continue;
        const [wx, wz] = axialToWorld(s.axial_q, s.axial_r, hr);
        const size = (s.scale ?? 1.0) * base;
        this._sunPositions.push({ entityId: s.entity_id, wx, wz, baseSize: size });
      }
    }
  }

  update(dt: number): void {
    if (!this._visible || this._sunPositions.length === 0) return;

    const count = this._sunPositions.length;

    // Text 1x/Sekunde aktualisieren
    this._textTimer += dt;
    if (this._textTimer >= 1) {
      this._textTimer = 0;
      const now = Date.now();
      this._billboard.clearAtlas();
      for (let i = 0; i < count; i++) {
        const entry = this._lifecycle?.get(this._sunPositions[i].entityId);
        if (entry && HIDDEN_STATES.has(entry.state)) continue;
        const [r, g, b] = this._stateColor(entry);
        const text = entry ? this._formatEntry(entry, now) : '—';
        this._billboard.writeSlot(i, text, r, g, b);
      }
      this._billboard.uploadAtlas();
    }

    // Instanzen jeden Frame (scaleMul ändert sich bei Lifecycle-Transitions)
    this._billboard.resetInstances();
    for (let i = 0; i < count; i++) {
      const sun = this._sunPositions[i];
      const entry = this._lifecycle?.get(sun.entityId);
      if (entry && HIDDEN_STATES.has(entry.state)) continue;
      const [cr, cg, cb] = this._stateColor(entry);
      this._billboard.addInstance(
        sun.wx, sun.baseSize, sun.wz,
        sun.baseSize, entry?.scaleMul ?? 1.0, i,
        cr, cg, cb,
      );
    }
    this._billboard.flushInstances();
  }

  private _stateColor(entry: SunLifecycleEntry | undefined): readonly [number, number, number] {
    if (!entry) return DEFAULT_COLOR;
    return STATE_COLORS[entry.state] ?? DEFAULT_COLOR;
  }

  private _formatEntry(e: SunLifecycleEntry, now: number): string {
    const sec = Math.max(0, (e.destructionTime - now) / 1000);
    const stateStr = STATE_NAMES[e.state] ?? '';
    // Show state explicitly in test mode equivalent (sec < 60) because test mode uses 40s total time
    if (sec < 60) {
      const fateStr = e.fate === 'black_hole' ? 'BH' : 'EXP';
      return `[${stateStr}-${fateStr}] ⏱ ${Math.ceil(sec)}s`;
    }
    if (sec < 3600) return `[${stateStr}] ⏱ ${Math.floor(sec / 60)}m ${Math.floor(sec % 60)}s`;
    if (sec < 86400) return `[${stateStr}] ⏱ ${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
    return `[${stateStr}] ⏱ ${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
  }

  dispose(): void {
    document.removeEventListener('keydown', this._onKey);
  }
}
